/**
 * projectCacheService.js — 项目节点数据本地缓存（IndexedDB + 本地文件夹）
 *
 * ══════════════════════════════════════════════════════════════════
 * 🎯 目标：图板切换秒开（< 200ms），消除 Loading 遮罩等待
 *
 * 原理：
 *   切换图板时，先从 IndexedDB 读取缓存的 nodes/edges/viewport，
 *   立即渲染（秒开），然后后台静默检查服务器 version，
 *   如果版本不同才拉取最新数据并更新。
 *
 * 存储结构：
 *   - L1: IndexedDB (CineartProjectCache / projects)
 *   - L2: 本地文件夹 (如已配置) {rootFolder}/_project_backup/{projectId}.json
 *   
 *   Key: projectId (number)
 *   Value: { nodes, edges, viewport, version, updatedAt }
 *
 * 🆕 双层备份机制（2026-03-13）：
 *   写入：同时写入 IndexedDB + 本地文件夹（如已配置）
 *   读取：优先 IndexedDB → 回退到本地文件夹 → 无缓存
 *   目的：浏览器缓存被清空后，可从本地文件夹恢复节点数据
 *
 * 写入时机：
 *   - 项目从服务器加载完成后
 *   - 每次 autoSave / 手动保存成功后
 *
 * 读取时机：
 *   - 切换图板时，优先读缓存，后台校验版本
 *
 * 缓存淘汰：
 *   - IndexedDB: 最多缓存 50 个项目（LRU 淘汰最早的）
 *   - 本地文件夹: 不自动淘汰（用户手动管理）
 *   - 删除项目时同步清除两层缓存
 * ══════════════════════════════════════════════════════════════════
 */

import localCacheService from './localCacheService';

const DB_NAME = 'CineartProjectCache';
const DB_VERSION = 1;
const STORE_NAME = 'projects';
const MAX_CACHED_PROJECTS = 50;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => { dbPromise = null; reject(request.error); };
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'projectId' });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
  return dbPromise;
}

/**
 * 读取项目缓存
 * @param {number|string} projectId
 * @returns {Promise<{nodes, edges, viewport, version, updatedAt}|null>}
 */
async function get(projectId) {
  // L1: 优先从 IndexedDB 读取（最快）
  try {
    const db = await openDB();
    const indexedDBData = await new Promise((resolve) => {
      const tx = db.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(Number(projectId));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    
    if (indexedDBData) {
      console.log(`⚡ [projectCache] L1 命中 (IndexedDB): 项目 ${projectId}`);
      return indexedDBData;
    }
  } catch (e) {
    console.warn('[projectCache] IndexedDB 读取失败:', e.message);
  }

  // L2: IndexedDB 未命中，尝试从本地文件夹读取（浏览器缓存被清空时的备份）
  try {
    const localData = await readFromLocalFolder(projectId);
    if (localData) {
      // 回填到 IndexedDB，下次更快
      put(projectId, localData).catch(() => {});
      return localData;
    }
  } catch (e) {
    console.warn('[projectCache] 本地文件夹读取失败:', e.message);
  }

  // 新项目或首次打开时无本地缓存是正常情况，不应显示为错误。
  console.log(`ℹ️ [projectCache] 缓存未命中（首次加载）: 项目 ${projectId}`);
  return null;
}

/**
 * 写入项目缓存（保存时调用）
 * @param {number|string} projectId
 * @param {{nodes: Array, edges: Array, viewport: Object, version: number, crewId?: number, crewName?: string}} data
 *
 * 🆕 crewId/crewName：可选。存进去之后，下次 Plan A 秒开时
 *    resolveLocalOnly 能拿到这两个值去查 L2 本地文件夹，
 *    避免「Plan A 渲染时 L2 miss → 第二次 Plan B 渲染才命中」的双闪问题。
 */
async function put(projectId, { nodes, edges, viewport, version, crewId, crewName }) {
  const cacheData = {
    projectId: Number(projectId),
    nodes,
    edges,
    viewport,
    version: version || 0,
    crewId: crewId || null,
    crewName: crewName || null,
    updatedAt: Date.now(),
  };

  // L1: 写入 IndexedDB（必须成功）
  try {
    const db = await openDB();
    const tx = db.transaction([STORE_NAME], 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(cacheData);
    // LRU 淘汰：超出上限时删除最旧的
    const countReq = store.count();
    countReq.onsuccess = () => {
      if (countReq.result > MAX_CACHED_PROJECTS) {
        const idx = store.index('updatedAt');
        const cursor = idx.openCursor();
        let toDelete = countReq.result - MAX_CACHED_PROJECTS;
        cursor.onsuccess = (e) => {
          const c = e.target.result;
          if (c && toDelete > 0) {
            store.delete(c.primaryKey);
            toDelete--;
            c.continue();
          }
        };
      }
    };
  } catch (e) {
    console.warn('[projectCache] IndexedDB 写入失败:', e.message);
  }

  // L2: 后台异步写入本地文件夹（如已配置，失败不影响正常流程）
  writeToLocalFolder(projectId, cacheData).catch(() => {});
}

/**
 * 删除项目缓存
 * @param {number|string} projectId
 */
async function remove(projectId) {
  // 删除 IndexedDB 缓存
  try {
    const db = await openDB();
    const tx = db.transaction([STORE_NAME], 'readwrite');
    tx.objectStore(STORE_NAME).delete(Number(projectId));
  } catch (e) {
    console.warn('[projectCache] IndexedDB 删除失败:', e.message);
  }

  // 删除本地文件夹备份
  deleteFromLocalFolder(projectId).catch(() => {});
}

// ==================== 本地文件夹读写（L2 层备份）====================

/**
 * 写入项目数据到本地文件夹
 * 文件路径：{rootFolder}/_project_backup/{projectId}.json
 */
async function writeToLocalFolder(projectId, data) {
  if (!localCacheService.isFileSystemAccessSupported()) return;

  try {
    // 获取已授权的根文件夹 handle（内部方法，需从 localCacheService 暴露）
    const rootHandle = await localCacheService._getAuthorizedRootHandle();
    if (!rootHandle) return; // 未配置本地缓存，跳过

    // 获取或创建 _project_backup 文件夹
    const backupFolder = await rootHandle.getDirectoryHandle('_project_backup', { create: true });

    // 写入 JSON 文件
    const fileName = `${projectId}.json`;
    const fileHandle = await backupFolder.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(data, null, 2));
    await writable.close();
  } catch (e) {
    // 权限问题、文件夹被删除等，静默失败不影响正常流程
    console.debug('[projectCache] 本地文件夹写入失败:', e.message);
  }
}

/**
 * 从本地文件夹读取项目数据
 */
async function readFromLocalFolder(projectId) {
  if (!localCacheService.isFileSystemAccessSupported()) return null;

  try {
    const rootHandle = await localCacheService._getAuthorizedRootHandle();
    if (!rootHandle) return null;

    const backupFolder = await rootHandle.getDirectoryHandle('_project_backup', { create: false });
    const fileName = `${projectId}.json`;
    const fileHandle = await backupFolder.getFileHandle(fileName, { create: false });
    const file = await fileHandle.getFile();
    const text = await file.text();
    const data = JSON.parse(text);

    return data;
  } catch (e) {
    // 文件不存在、权限问题等，返回 null
    return null;
  }
}

/**
 * 从本地文件夹删除项目备份
 */
async function deleteFromLocalFolder(projectId) {
  if (!localCacheService.isFileSystemAccessSupported()) return;

  try {
    const rootHandle = await localCacheService._getAuthorizedRootHandle();
    if (!rootHandle) return;

    const backupFolder = await rootHandle.getDirectoryHandle('_project_backup', { create: false });
    const fileName = `${projectId}.json`;
    await backupFolder.removeEntry(fileName);

    console.log(`🗑️ [projectCache] 已删除本地备份: ${fileName}`);
  } catch (e) {
    // 文件不存在、权限问题等，静默失败
    console.debug('[projectCache] 本地备份删除失败:', e.message);
  }
}

const projectCacheService = { get, put, remove };
export default projectCacheService;
